import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Pencil, Send, Link2, Download, Printer, Ban, Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Panel, StatusBadge, Loading, Field, EmptyState } from "@/components/common";
import DocView from "@/components/DocView";
import { deliveryToast } from "@/pages/EstimateDetail";
import { api, errMsg, download } from "@/lib/api";
import { fmtDay, money, METHOD_LABELS, todayKey } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

function PaymentDialog({ open, onOpenChange, inv, onSaved }) {
  const { tz } = useAuth();
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) { setF({ amount: inv.balance, method: "cash", date: todayKey(tz), reference: "", note: "" }); setError(""); }
  }, [open, inv.balance, tz]);

  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post(`/invoices/${inv.id}/payments`, { ...f, amount: Number(f.amount) });
      toast.success("Payment recorded");
      onSaved(data);
      onOpenChange(false);
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="payment-dialog">
        <DialogHeader><DialogTitle className="font-display">Record payment</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount ($)" htmlFor="p-amt"><Input id="p-amt" type="number" min="0.01" step="0.01" max={inv.balance} required value={f.amount ?? ""} onChange={(e) => set("amount", e.target.value)} className="h-11" data-testid="payment-amount-input" /></Field>
            <Field label="Method">
              <Select value={f.method} onValueChange={(v) => set("method", v)}>
                <SelectTrigger className="h-11" data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["cash", "check", "bank_transfer"].map((m) => <SelectItem key={m} value={m}>{METHOD_LABELS[m]}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Date" htmlFor="p-date"><Input id="p-date" type="date" required value={f.date || ""} onChange={(e) => set("date", e.target.value)} className="h-11" data-testid="payment-date-input" /></Field>
            <Field label="Reference" htmlFor="p-ref"><Input id="p-ref" placeholder="Check # / transfer ID" value={f.reference || ""} onChange={(e) => set("reference", e.target.value)} className="h-11" data-testid="payment-reference-input" /></Field>
          </div>
          <p className="text-xs text-slate-500">Online card payments can't be entered manually. They are recorded only after the payment provider confirms them.</p>
          {error && <p className="text-sm text-red-700" role="alert" data-testid="payment-error">{error}</p>}
          <DialogFooter><Button type="submit" disabled={busy} data-testid="payment-submit-btn">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Record payment</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function InvoiceDetail() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pay, setPay] = useState(false);

  const load = useCallback(() => {
    api.get(`/invoices/${id}`).then((r) => setInv(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const send = (email) => act(async () => {
    const { data } = await api.post(`/invoices/${id}/send`, { email });
    if (email) deliveryToast(data.delivery);
    else { await navigator.clipboard?.writeText(data.link).catch(() => {}); toast.success("Invoice link copied"); }
    load();
  });
  const voidInv = () => act(async () => { const { data } = await api.post(`/invoices/${id}/void`); setInv(data); toast.success("Invoice voided"); });
  const delPay = (pid) => act(async () => { const { data } = await api.delete(`/invoices/${id}/payments/${pid}`); setInv(data); toast.success("Payment removed"); });

  if (!inv) return <Loading />;
  const isVoid = inv.status === "void";

  return (
    <div data-testid="invoice-detail-page">
      <Link to="/invoices" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4"><ArrowLeft className="h-4 w-4" />Invoices</Link>
      <PageHeader title={`Invoice ${inv.number}`} subtitle={`${inv.client_name} · Issued ${fmtDay(inv.created_at)} · Due ${fmtDay(inv.due_date)}`} actions={<StatusBadge status={inv.payment_status} testId="invoice-status" />} />
      <div className="flex flex-wrap gap-2 mb-6">
        {!isVoid && <Button variant="outline" asChild className="h-11"><Link to={`/invoices/${id}/edit`} data-testid="edit-invoice-btn"><Pencil className="h-4 w-4 mr-1" />Edit</Link></Button>}
        {!isVoid && <Button className="h-11" disabled={busy} onClick={() => send(true)} data-testid="send-invoice-btn"><Send className="h-4 w-4 mr-1" />{inv.status === "draft" ? "Send by email" : "Resend email"}</Button>}
        {!isVoid && <Button variant="outline" className="h-11" disabled={busy} onClick={() => send(false)} data-testid="copy-invoice-link-btn"><Link2 className="h-4 w-4 mr-1" />{inv.status === "draft" ? "Mark sent & copy link" : "Copy link"}</Button>}
        <Button variant="outline" className="h-11" disabled={busy} onClick={() => act(() => download(`/invoices/${id}/pdf`, `${inv.number}.pdf`))} data-testid="download-pdf-btn"><Download className="h-4 w-4 mr-1" />PDF</Button>
        {inv.status !== "draft" && <Button variant="outline" asChild className="h-11"><a href={`/i/${inv.public_token}`} target="_blank" rel="noreferrer" data-testid="print-invoice-btn"><Printer className="h-4 w-4 mr-1" />Printable view</a></Button>}
        {!isVoid && inv.status !== "draft" && inv.balance > 0 && <Button className="h-11" onClick={() => setPay(true)} data-testid="record-payment-btn"><Plus className="h-4 w-4 mr-1" />Record payment</Button>}
        {!isVoid && inv.amount_paid === 0 && <Button variant="outline" className="h-11 text-red-700 hover:bg-red-50 hover:text-red-800" disabled={busy} onClick={voidInv} data-testid="void-invoice-btn"><Ban className="h-4 w-4 mr-1" />Void</Button>}
      </div>
      {inv.status === "draft" && <p className="text-sm text-slate-600 mb-4" data-testid="draft-note">This invoice is a draft. Send it (or copy the link) before recording payments.</p>}
      <div className="grid lg:grid-cols-3 gap-4 md:gap-6">
        <Panel className="lg:col-span-2"><DocView doc={inv} /></Panel>
        <Panel title="Payments" testId="payments-panel">
          {inv.payments.length === 0 ? <EmptyState title="No payments yet" /> : inv.payments.map((p) => (
            <div key={p.id} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0" data-testid={`payment-row-${p.id}`}>
              <div><div className="font-medium text-slate-900">{money(p.amount)}</div><div className="text-xs text-slate-500">{fmtDay(p.date)} · {METHOD_LABELS[p.method]}{p.reference ? ` · ${p.reference}` : ""}</div></div>
              {p.method !== "online" && <Button variant="ghost" size="icon" className="h-11 w-11" onClick={() => delPay(p.id)} aria-label="Remove payment" data-testid={`delete-payment-${p.id}`}><Trash2 className="h-4 w-4" /></Button>}
            </div>
          ))}
        </Panel>
      </div>
      <PaymentDialog open={pay} onOpenChange={setPay} inv={inv} onSaved={setInv} />
    </div>
  );
}
